<template>
  <div class="flex bg-white dark:bg-charcoal-900 relative">
    <!-- Vertical Tab Rail -->
    <nav class="w-24 bg-gray-100 dark:bg-charcoal-700 flex flex-col items-center py-4 space-y-2 px-2.5">
      <button v-for="tab in panelTabs" :key="tab.id" @click="onTabClick(tab.id)" class="w-full rounded-xl py-2.5 text-charcoal dark:text-gray-200 flex flex-col gap-1 items-center justify-center text-center transition-colors duration-500" :class="[
            activeTab === tab.id ? 'bg-blue-100 text-heroblue-700 dark:bg-blue/20 dark:text-white' : 'bg-transparent hover:bg-gray-200 dark:hover:bg-charcoal-600/75'
          ]">
        <u-icon :name="tab.icon" class="text-2xl" />
        <span class="text-xs font-bold leading-tight">{{tab.label}}</span>
      </button>
    </nav>

    <!-- Left Panel (dynamic per tab) -->
    <aside class="transition-all duration-200 relative flex flex-col py-1" :class="showPanel ? 'w-72 opacity-100' : 'w-0 opacity-0 pointer-events-none'">
      <transition mode="out-in" name="f-fade">
        <div v-if="showPanel" class="flex flex-col h-full">

          <template v-if="contextualPanelComponent">
            <component :is="contextualPanelComponent" v-bind="contextualPanelProps" />
          </template>

          <surface-editor-tabs-templates v-if="!contextualPanel && activeTab === 'templates'" />
          <keep-alive v-if="photosTabComponent">
            <component
              :is="photosTabComponent"
              v-if="!contextualPanel && activeTab === 'photos'"
              v-bind="photosTabComponentProps"
            />
          </keep-alive>
          <surface-editor-tabs-text v-if="!contextualPanel && activeTab === 'text'" />
          <surface-editor-tabs-shapes v-if="!contextualPanel && activeTab === 'shapes'" />

        </div>
      </transition>
    </aside>

    <button v-if="!panelCollapsed" class="bg-white dark:bg-charcoal-800 absolute top-1/2 right-0 translate-x-1/2 rounded-full w-5 h-10 flex items-center justify-center shadow text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white transition-colors duration-150 z-10 ring-1 ring-gray-300 dark:ring-charcoal-600" @click="collapsePanel">
      <u-icon name="i-ri-arrow-left-s-line" class="text-base"/>
    </button>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {markRaw} from 'vue';
  import SurfaceEditorContextualColorPanel from '~/components/surface-editor/contextual-panels/color.vue';

  const props = defineProps({
    photosTabComponent: {
      type: [Object, null],
      default: null
    },
    photosTabComponentProps: {
      type: Object,
      default: () => ({})
    }
  });

  const contextualPanelRegistry = {
    'color': markRaw(SurfaceEditorContextualColorPanel)
  };

  const surfaceEditorStore = useSurfaceEditorStore();
  const {contextualPanel} = storeToRefs(surfaceEditorStore);

  const panelTabs = [
    { id: 'templates', label: 'Templates', icon: 'i-ri-layout-fill' },
    props.photosTabComponent && { id: 'photos',    label: 'Photos',    icon: 'i-ri-image-fill' },
    { id: 'text',      label: 'Text',      icon: 'i-ri-text' },
    { id: 'shapes',    label: 'Shapes',    icon: 'i-ri-shape-2-fill' },
  ].filter(Boolean);


  const userActiveTab = ref('templates');
  const previousTab = ref(userActiveTab.value);
  const panelCollapsed = ref(false);
  const activeTab = computed(() => contextualPanel.value ? null : userActiveTab.value);
  const showPanel = computed(() => !panelCollapsed.value && (!!contextualPanel.value || !!activeTab.value));
  const panelKey = computed(() => contextualPanel.value ? `contextual-panel-${contextualPanel.value.id}` : activeTab.value);

  const contextualPanelComponent = computed(() => {
    if (!contextualPanel.value) return null;

    return contextualPanelRegistry[contextualPanel.value.id] || null;
  });

  const contextualPanelProps = computed(() => contextualPanel.value?.props || {});

  function onTabClick(id) {
    if (!contextualPanel.value && activeTab.value === id) {
      collapsePanel();
      return;
    }

    if (contextualPanel.value) {
      surfaceEditorStore.closeContextualPanel();
    }

    if (panelCollapsed.value) {
      // If panel is collapsed, clicking any tab:
      // 1) expands the panel, 2) sets that tab active
      panelCollapsed.value = false;
      userActiveTab.value = id;
      return
    }

    // Switch active tab
    userActiveTab.value = id;
  }

  function collapsePanel() {
    panelCollapsed.value = true;
    if (contextualPanel.value) {
      surfaceEditorStore.closeContextualPanel();
      userActiveTab.value = previousTab.value;
      return;
    }

    userActiveTab.value = null;
  }

  watch(contextualPanel, (panel) => {
    if (panel) {
      previousTab.value = userActiveTab.value;
      panelCollapsed.value = false;
      return;
    }

    userActiveTab.value = userActiveTab.value || previousTab.value || 'templates';
  });

</script>
